import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { type FC, type ReactNode, useState } from "react";

import { Small } from "@fronterahealth/frontera-ui-components";

import { type QAPreviewPackage, type QAPreviewRule } from "@api/rest/types-and-hooks";
import { StillBadge } from "@pages/QARules/StillBadge";
import { type PackageSource, type TurnedOffBy } from "@pages/QARules/constants";
import { PACKAGE_TYPE_BADGE, SOURCE_LABEL, TURNED_OFF_LABEL, packageTypeOf } from "@pages/QARules/qaRuleLabels";

interface PreviewPackageRowProps {
  pkg: QAPreviewPackage;
  /** Controls at the right of the package header (e.g. on/off switch, edit link). */
  packageAction?: ReactNode;
  /** Controls at the right of each rule. */
  renderRuleAction?: (rule: QAPreviewRule) => ReactNode;
}

/** One package in a QA preview: why it applies, and which of its rules get sent. */
export const PreviewPackageRow: FC<PreviewPackageRowProps> = ({ pkg, packageAction, renderRuleAction }) => {
  const [expanded, setExpanded] = useState(false);
  const included = pkg.rules.filter((r) => r.included).length;
  const packageOff = included === 0;
  const typeBadge = PACKAGE_TYPE_BADGE[packageTypeOf(pkg.package_type)];

  return (
    <div className={clsx("rounded-lg border border-border-subtle", packageOff ? "bg-bg-default" : "bg-bg-surface")}>
      <div className="flex items-start gap-3 px-4 py-3">
        <button
          type="button"
          className="mt-0.5 flex-shrink-0 text-text-tertiary hover:text-text-primary"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? "Collapse package" : "Expand package"}
        >
          {expanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={clsx("font-semibold", packageOff ? "text-text-tertiary line-through" : "text-text-primary")}
            >
              {pkg.name}
            </span>
            <StillBadge text={typeBadge.label} appearance={typeBadge.appearance} />
            {pkg.sources.map((source) => (
              <StillBadge key={source} text={SOURCE_LABEL[source as PackageSource] ?? source} appearance="processing" />
            ))}
          </div>
          <Small colorType="secondary">
            {included} of {pkg.rules.length} rule{pkg.rules.length === 1 ? "" : "s"} sent
          </Small>
        </div>
        {packageAction && <div className="flex flex-shrink-0 items-center gap-3">{packageAction}</div>}
      </div>

      {expanded && (
        <div className="border-t border-border-subtle">
          {pkg.rules.map((row) => {
            const { location } = row.rule;
            return (
              <div
                key={row.rule.id}
                className="flex items-start justify-between gap-4 border-t border-border-subtle px-4 py-2.5 pl-11 first:border-t-0"
              >
                <div className="min-w-0">
                  <div
                    className={clsx(
                      "truncate text-sm font-medium",
                      row.included ? "text-text-primary" : "text-text-tertiary line-through",
                    )}
                  >
                    {row.rule.name || "Untitled rule"}
                  </div>
                  <Small colorType="secondary">
                    {[row.rule.category || pkg.name, location].filter(Boolean).join(" · ")}
                    {row.turned_off_by &&
                      ` — ${TURNED_OFF_LABEL[row.turned_off_by as TurnedOffBy] ?? row.turned_off_by}`}
                  </Small>
                </div>
                {renderRuleAction && (
                  <div className="flex flex-shrink-0 items-center gap-3">{renderRuleAction(row)}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
